import React, { useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  backdropBlur?: boolean;
  stacked?: boolean;
}
const sizeStyles = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl'
};
let modalCount = 0;
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  backdropBlur = false,
  stacked = false
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      modalCount++;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        modalCount--;
        if (modalCount === 0) {
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
        }
      };
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const backdropZ = stacked ? 'z-[10000]' : 'z-[9990]';
  const modalZ = stacked ? 'z-[10010]' : 'z-[9999]';
  return createPortal(<Fragment data-id="element-208">
      {/* Backdrop */}
      <div className={`fixed inset-0 bg-black/40 ${backdropZ} transition-opacity ${backdropBlur ? 'backdrop-blur-[6px]' : ''}`} onClick={onClose} aria-hidden="true" data-id="element-209" />

      {/* Modal */}
      <div className={`fixed inset-0 ${modalZ} overflow-y-auto`} data-id="element-210">
        <div className="flex min-h-full items-center justify-center p-4" data-id="element-211">
          <div className={`
              relative bg-white rounded-xl shadow-xl w-full
              transform transition-all
              ${sizeStyles[size]}
            `} role="dialog" aria-modal="true" aria-labelledby={title ? 'modal-title' : undefined} data-id="element-212">
            {/* Header */}
            {title && <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-213">
                <h2 id="modal-title" className="text-lg font-semibold text-gray-900" data-id="element-214">
                  {title}
                </h2>
                <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors" aria-label="Cerrar" data-id="element-215">
                  <XIcon className="w-5 h-5" data-id="element-216" />
                </button>
              </div>}

            {/* Content */}
            <div className={title ? '' : 'pt-4'} data-id="element-217">{children}</div>
          </div>
        </div>
      </div>
    </Fragment>, document.body);
}
export function ModalBody({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`px-6 py-4 ${className}`} data-id="element-218">{children}</div>;
}
export function ModalFooter({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 ${className}`} data-id="element-219">
      {children}
    </div>;
}